/**
 * Global Functionality
 * The logic in this is intended to ONLY contain any functionality
 * that is required on every page load.
 *
 * NOTE: Please do not include any page-specific logic here.
 **/

import Cart from "./cart";
import Logger from "../lib/logger";
import ThemeFooter from "../partials/ThemeFooter";
import ThemeHeader from "../partials/ThemeHeader";
import ThemeMiniCart from "../partials/ThemeMiniCart";
import ThemeScreensaver from "../partials/ThemeScreensaver";

import {
	autocomplete,
	search,
	fromLiquidTemplate,
	defaultLiquidTemplate,
	priceDecorator,
} from "@nosto/autocomplete/liquid";

export default function () {
	const NAME = 'Global';
	Logger.info(`${NAME} > loading`);

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	const cart = new Cart();

	const themeHeaderDom = document.querySelector(`[data-theme-header]`);

	const themeMiniCartDom = document.querySelector(`[data-theme-mini-cart]`);
	const themeScreensaverDom = document.querySelector(`[data-theme-screensaver]`);

	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * init --- Initialization block
	 */
	function init() {
		Logger.info(`${NAME} > init`);
		initFooter();
		initHeader();
		initMiniCart();
		initScreensaver();
		initZonos();
		initIntercom();
		initNostoAutocomplete();

		// Get the current cart contents
		cart.get();

		return {
			cart: cart,
		};
	}

	/**
	 * initHeader - Initialize the main header
	 */
	function initHeader() {
		Logger.info(`${NAME} > initHeader`);
		if (!themeHeaderDom) return;
		const el = new ThemeHeader(themeHeaderDom, cart);
		el.init();
	}

	/**
	 * initFooter - Initialize the main footer
	 */
	function initFooter() {
		Logger.info(`${NAME} > initFooter`);
		const el = new ThemeFooter();
		el.init();
	}

	/**
	 * initMiniCart - Initialize the main mini cart
	 */
	const initMiniCart = () => {
		if (!themeMiniCartDom) return; 
		Logger.info(`${NAME} > initMiniCart`);
		const miniCart = new ThemeMiniCart(themeMiniCartDom, cart);
		miniCart.init();
	};

	const initScreensaver = () => {
		Logger.info(`${NAME} > initScreensaver`);

		if (!themeScreensaverDom) return;

		const el = new ThemeScreensaver(themeScreensaverDom);
		el.init();
	}

	const initZonos = () => {
		window.addEventListener('load', function () {
			// Make sure the navbar is synced up with zonos
			if (document.getElementById('countrySelect') && document.getElementById('countrySelect').value !== localStorage.getItem('zonos-country')) {
				document.querySelectorAll('.zonos-currency').forEach(function (el) {
					el.innerHTML = document.getElementById('countrySelect').value;
				});
			} else {
				document.querySelectorAll('.zonos-currency').forEach(function (el) {
					el.innerHTML = localStorage.getItem('zonos-country') || 'US';
				});
			}

			zonos.config({ showDecimal: false });
			document.querySelectorAll('.zonos-trigger').forEach(function (el) {
				el.addEventListener('click', function () {
					// Open zonos dialog box
					document.querySelector('a.z-flag').click();
					document.querySelector('#zonos').style.display = 'block';

					// Close mobile left side menu
					if (document.querySelector('.theme-header-drawer-desktop[aria-expanded="true"]')) {
						document.querySelector('.theme-header-drawer-mobile__close-button').click();
					}
				});
			});

			document.addEventListener('input', function (e) {
				// Change the country code in the nav menus when a new country is selected in the zonos dialog box
				if (event.target.id !== 'countrySelect') return;
				document.querySelectorAll('.zonos-currency').forEach(function (el) {
					el.innerHTML = event.target.value;
				});

				// Set local storage
				localStorage.setItem('zonos-country', document.getElementById('countrySelect').value);
			}, false);
		});
	};

	const initIntercom = () => {
		var w = window;
		var ic = w.Intercom;
		if (typeof ic === "function") {
			ic("reattach_activator");
			ic("update", w.intercomSettings);
		} else {
			var d = document;
			var i = function () {
				i.c(arguments);
			};
			i.q = [];
			i.c = function (args) {
				i.q.push(args);
			};
			w.Intercom = i;
			var l = function () {
				var s = d.createElement("script");
				s.type = "text/javascript";
				s.async = true;
				s.src = "https://widget.intercom.io/widget/oz9mq4eu";
				var x = d.getElementsByTagName("script")[0];
				x.parentNode.insertBefore(s, x);
			};
			setTimeout(() => {
				l();
			}, 8000);
		}
	};

	const initNostoAutocomplete = () => {
		Logger.info(`${NAME} > initNostoAutocomplete`);

		const ireneLiquidTemplate = "\n{% assign hasKeywords = response.keywords.hits.length > 0 %}\n{% assign hasProducts = response.products.hits.length > 0 %}\n{% assign hasHistory = history.length > 0 %}\n{% assign imagePlaceHolder = 'https://cdn.nosto.com/nosto/9/mock' %}\n\n<div class=\"ns-autocomplete-results\">\n  {% if hasKeywords == false and hasProducts == false and hasHistory %}\n    <div class=\"ns-autocomplete-history\">\n      <div class=\"ns-autocomplete-header\">\n        Recently searched\n      </div>\n      {% for hit in history %}\n        <div class=\"ns-autocomplete-history-item\" data-ns-hit=\"{{ hit | json | escape }}\" data-testid=\"history\">\n          {{ hit.item }}\n          <a\n            href=\"#\"\n            class=\"ns-autocomplete-history-item-remove\"\n            data-ns-remove-history=\"{{hit.item}}\">\n            &#x2715;\n          </a>\n        </div>\n      {% endfor %}\n    </div>\n    <div class=\"ns-autocomplete-history-clear\">\n      <button\n        type=\"button\"\n        class=\"ns-autocomplete-button\"\n        data-ns-remove-history=\"all\">\n        Clear history\n      </button>\n    </div>\n  {% elsif hasKeywords or hasProducts %}\n    {% if hasKeywords %}\n      <div class=\"ns-autocomplete-keywords\">\n        <div class=\"ns-autocomplete-header\">\n          Keywords\n        </div>\n        {% for hit in response.keywords.hits %}\n          <div class=\"ns-autocomplete-keyword\" data-ns-hit=\"{{ hit | json | escape }}\" data-testid=\"keyword\">\n            {% if hit._highlight and hit._highlight.keyword %}\n              <span>{{ hit._highlight.keyword }}</span>\n            {% else %}\n              <span>{{ hit.keyword }}</span>\n            {% endif %}\n          </div>\n        {% endfor %}\n      </div>\n    {% endif %}\n    {% if hasProducts %}\n      <div class=\"ns-autocomplete-products\">\n        <div class=\"ns-autocomplete-header\">\n          Products\n        </div>\n        {% for hit in response.products.hits %}\n          <a\n            class=\"ns-autocomplete-product\"\n            href=\"{{ hit.url }}\"\n            data-ns-hit=\"{{ hit | json | escape }}\"\n            data-testid=\"product\">\n            <img\n              class=\"ns-autocomplete-product-image\"\n              src=\"{% if hit.imageUrl %}{{ hit.imageUrl }}{% else %}{{ imagePlaceHolder }}{% endif %}\"\n              alt=\"{{ hit.name }}\"\n              width=\"60\"\n              height=\"40\" />\n            <div class=\"ns-autocomplete-product-info\">\n              {% if hit.brand %}\n                <div class=\"ns-autocomplete-product-brand\">\n                  {{ hit.brand }}\n                </div>\n              {% endif %}\n              <div class=\"ns-autocomplete-product-name\">\n                {{ hit.name }}\n              </div>\n              <div>\n                <span>\n                  {{ hit.price }}&euro;\n                </span>\n                {% if hit.listPrice and hit.listPrice != hit.price %}\n                    <span class=\"ns-autocomplete-product-list-price\">\n                        {{ hit.listPrice }}\n                        &euro;\n                    </span>\n                {% endif %}\n              </div>\n            </div>\n          </a>\n        {% endfor %}\n      </div>\n    {% endif %}\n    <div class=\"ns-autocomplete-submit\">\n      <button type=\"submit\" class=\"ns-autocomplete-button\">\n        See all search results\n      </button>\n    </div>\n  {% endif %}\n</div>\n";

		// Initialize autocomplete with fetch configuration
		autocomplete({
			fetch: {
				products: {
					fields: [
						"name",
						"url",
						"imageUrl",
						"price",
						"listPrice",
						"brand",
						"priceCurrencyCode", // Include priceCurrencyCode for proper formatting
					],
					size: 4, // Adjust to desired number of product results
					variationId: "SEK"
				},
				keywords: {
					size: 5, // Adjust to desired number of keyword results
					fields: ["keyword", "_highlight.keyword"],
					highlight: {
						preTag: `<strong>`,
						postTag: "</strong>",
					},
				},
			},
			inputSelector: "#search", // The search input field
			dropdownSelector: "#search-results", // The container for search results
			hitDecorators: [
				// Log before applying the priceDecorator
				(hit) => {
					console.log("Before priceDecorator:", hit);
					return hit; // Return the hit data unmodified for now
				},

				// Applies the priceDecorator
				priceDecorator({ defaultCurrency: "USD" }),

				// Log after applying the priceDecorator
				(hit) => {

					// Set timer to re-run Zonos config
					setTimeout(() => {
						Logger.info(`${NAME} > initNostoAutocomplete > zonos.config`);
						zonos.config({ currencySelectors: ".ns-autocomplete-product-info > div:nth-child(3) > span" });

						document.querySelectorAll('.ns-autocomplete-product-info > div:nth-child(3) > span').forEach(function (el) {
							// remove decimals
							el.innerHTML = el.innerHTML.replace(/\.00/g, '');
						});
					}, 100);

					console.log("After priceDecorator:", hit);
					return hit; // Return the modified hit data after decoration
				},
			],
			render: fromLiquidTemplate(ireneLiquidTemplate), // Default Liquid template for rendering
			submit: async (query, config, options) => {
				if (query.length >= config.minQueryLength) {
					const response = await search(
						{ query },
						{
							redirect: false, // Set to false to handle results on the same page
							track: config.nostoAnalytics ? "serp" : undefined,
							...options,
						}
					);

					// Redirect to the search results page with the query as a parameter
					window.location.href = `/search?q=${encodeURIComponent(query)}`;
				}
			},
		});

		// Handle form submission manually
		const searchForm = document.getElementById("search-form");
		if (searchForm) {
			searchForm.addEventListener("submit", async (e) => {
				e.preventDefault(); // Prevent the form from submitting and reloading the page
				const query = document.getElementById("search").value;

				const config = {
					minQueryLength: 2, // Minimum length for a valid query
					nostoAnalytics: true, // Optional: track analytics
				};

				const options = {}; // Optional additional options

				if (query.length >= config.minQueryLength) {
					const response = await search(
						{ query },
						{
							redirect: false, // Set to false to handle results programmatically
							track: config.nostoAnalytics ? "serp" : undefined,
							...options,
						}
					);

					// Redirect to the search results page with the query as a parameter
					window.location.href = `/search?q=${encodeURIComponent(query)}`;
				}
			});
		}
	}


	// Do not edit below this line...
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	Logger.info(`${NAME} > loaded`);

	return init();
}
